import { Link } from 'react-router';

import type { InsurerResponse } from '../../api';
import { AsyncSection, DataTable, PageHeader, Panel, type Column } from '../../components';
import { formatDate } from '../../format';
import { PATHS } from '../../routes/paths';
import { useInsurers } from '../queries';
import { ONBOARDING_COPY } from './onboarding';

/**
 * The insurers we file to, and nothing else on the screen.
 *
 * A screen of its own now, where it used to be a panel at the bottom of the
 * companies list. The old reasoning was that nobody thinks about an insurer
 * except when a contract needs one -- which is true, and is exactly why it
 * needs an address somebody can be sent to. A registration form halfway down
 * another screen is a thing people find by accident, and the menu entry beside
 * "Empresas clientes" is where somebody onboarding a customer looks when the
 * contract form has no operadora to choose.
 *
 * Retired insurers stay listed. A contract signed years ago still points at one,
 * and a list that hid them would make an old contract look like it names
 * something that does not exist.
 */
export function InsurersPage() {
  const insurers = useInsurers();

  const columns: readonly Column<InsurerResponse>[] = [
    {
      key: 'code',
      header: ONBOARDING_COPY.insurerCodeLabel,
      render: (row) => <code>{row.code}</code>,
    },
    { key: 'name', header: ONBOARDING_COPY.insurerNameLabel, render: (row) => row.name },
    {
      key: 'layout',
      header: ONBOARDING_COPY.insurerLayoutLabel,
      render: (row) =>
        row.layout_code === null || row.layout_code === undefined || row.layout_code === '' ? (
          <span className="table__muted">{ONBOARDING_COPY.defaultLayout}</span>
        ) : (
          <code>{row.layout_code}</code>
        ),
    },
    {
      key: 'status',
      header: 'Situação',
      render: (row) => (row.is_active ? ONBOARDING_COPY.active : ONBOARDING_COPY.retired),
    },
    {
      key: 'created_at',
      header: 'Cadastrada em',
      align: 'end',
      render: (row) => formatDate(row.created_at),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={ONBOARDING_COPY.insurersTitle}
        description={ONBOARDING_COPY.insurersDescription}
        actions={
          <Link className="button button--primary" to={PATHS.staffInsurerNew}>
            {ONBOARDING_COPY.registerInsurer}
          </Link>
        }
      />

      <Panel
        title={ONBOARDING_COPY.insurersTitle}
        description={ONBOARDING_COPY.insurersDescription}
      >
        <AsyncSection
          state={insurers}
          loadingLabel={ONBOARDING_COPY.insurersLoading}
          errorTitle={ONBOARDING_COPY.insurersError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={ONBOARDING_COPY.noInsurers}
          emptyDescription={ONBOARDING_COPY.noInsurersDescription}
        >
          {(data) => (
            <DataTable
              id="insurers"
              caption={ONBOARDING_COPY.insurersCaption}
              columns={columns}
              rows={data.items}
              rowKey={(row) => row.id}
            />
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}
